import React, { useState, useEffect } from 'react'
import {
  Lock,
  Loader2,
  CheckCircle2,
  AlertCircle,
  KeyRound,
  ArrowLeft,
  Check,
  X,
} from 'lucide-react'
import { useLanguage } from '../../context/LanguageContext'
import { useToast } from '../../context/ToastContext'
import { updatePassword } from '../../services/authService'
import SEO from '../Common/SEO'
import { XlevenLogo } from '../Common/XlevenLogo'
import { AppFooter } from '../Common/AppFooter'
import { t } from '../../i18n'

interface ChangePasswordViewProps {
  onBack: () => void
  onSuccess: () => void
}

export const ChangePasswordView: React.FC<ChangePasswordViewProps> = ({
  onBack,
  onSuccess,
}) => {
  const { lang } = useLanguage()
  const { showToast } = useToast()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [error, setError] = useState('')
  const [showPasswordRequirements, setShowPasswordRequirements] =
    useState(false)
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  const [sameAsOldError, setSameAsOldError] = useState('')

  // Password validation
  const hasMinLength = password.length >= 8
  const hasLetters = /[A-Za-z]/.test(password)
  const hasNumbers = /\d/.test(password)
  const hasSpecial = /[@$!%*#?&]/.test(password)
  const isPasswordValid = hasMinLength && hasLetters && hasNumbers && hasSpecial

  useEffect(() => {
    if (error) setError('')
    if (password && confirmPassword) {
      if (password !== confirmPassword) {
        setPasswordError(t(lang, 'passwordMismatch'))
        setPasswordSuccess('')
      } else {
        setPasswordError('')
        setPasswordSuccess(t(lang, 'passwordMatch'))
      }
    } else {
      setPasswordError('')
      setPasswordSuccess('')
    }
  }, [password, confirmPassword, lang])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setSameAsOldError('')

    if (!password || !confirmPassword) {
      setError(t(lang, 'fillAllFields'))
      return
    }

    if (!isPasswordValid) {
      setError(t(lang, 'passwordInvalid'))
      return
    }

    if (password !== confirmPassword) {
      setError(t(lang, 'passwordMismatch'))
      return
    }

    setIsLoading(true)
    try {
      await updatePassword(password)
      setIsSuccess(true)
      showToast('success', t(lang, 'passwordChanged'))
      setTimeout(() => {
        onSuccess()
      }, 3000)
    } catch (err: any) {
      if (err.message?.includes('different from the old')) {
        setSameAsOldError(t(lang, 'passwordSameAsOld'))
      } else {
        setError(err.message || t(lang, 'failedToUpdatePassword'))
      }
    } finally {
      setIsLoading(false)
    }
  }

  if (isSuccess) {
    return (
      <div className='min-h-screen bg-white flex flex-col items-center justify-center p-8 text-center animate-in fade-in zoom-in-95 duration-500'>
        <div className='w-24 h-24 bg-emerald-50 rounded-[40px] flex items-center justify-center text-emerald-500 mb-8 border border-emerald-100 shadow-xl shadow-emerald-50'>
          <CheckCircle2
            size={48}
            strokeWidth={2.5}
          />
        </div>
        <h1 className='text-3xl font-black text-slate-900 mb-3 tracking-tighter uppercase'>
          {t(lang, 'passwordChanged')}
        </h1>
        <p className='text-slate-400 text-sm font-medium leading-relaxed max-w-xs mb-10'>
          {t(lang, 'passwordChangedDesc')}
        </p>
        <button
          onClick={onSuccess}
          className='w-full max-w-[320px] bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all active:scale-95 shadow-xl shadow-slate-100'
        >
          {t(lang, 'signInLabel')}
        </button>
      </div>
    )
  }

  return (
    <div className='min-h-screen bg-white flex flex-col p-0 relative'>
      <SEO title={t(lang, 'resetIntel')} />
      <div className='flex-1 flex flex-col items-center justify-center p-6 py-12'>
        <div className='w-full max-w-[420px] animate-in fade-in slide-in-from-bottom-8 duration-700'>
          <div className='flex flex-col items-center mb-10'>
            <div className='mb-8 scale-110 md:hidden'>
              <XlevenLogo />
            </div>
            <h1 className='text-3xl font-black text-slate-900 tracking-tighter mb-2 uppercase text-center'>
              {t(lang, 'updatePassword')}
            </h1>
            <p className='text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] text-center'>
              {t(lang, 'verifyIdentity')}
            </p>
          </div>

          <form
            onSubmit={handleSubmit}
            className='space-y-6'
          >
            <div className='space-y-4'>
              <div className='space-y-1.5'>
                <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                  {t(lang, 'newPassword')}
                </label>
                <div className='relative group'>
                  <KeyRound
                    className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                    size={18}
                  />
                  <input
                    type='password'
                    placeholder='••••••••'
                    className={`w-full bg-slate-50 border ${password && !isPasswordValid ? 'border-rose-200' : 'border-slate-100'} rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200`}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value)
                      setShowPasswordRequirements(true)
                    }}
                    onFocus={() => setShowPasswordRequirements(true)}
                    disabled={isLoading}
                    autoFocus
                  />
                </div>

                {showPasswordRequirements && password.length > 0 && (
                  <div className='px-1 py-2 space-y-2 animate-in fade-in slide-in-from-top-1'>
                    <div className='grid grid-cols-2 gap-x-4 gap-y-1.5'>
                      <div
                        className={`flex items-center gap-1.5 ${hasMinLength ? 'text-emerald-500' : 'text-slate-300'}`}
                      >
                        {hasMinLength ? (
                          <Check
                            size={10}
                            strokeWidth={3}
                          />
                        ) : (
                          <X
                            size={10}
                            strokeWidth={3}
                          />
                        )}
                        <span className='text-[9px] font-black uppercase tracking-tight'>
                          {t(lang, 'passwordMinLength')}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 ${hasLetters ? 'text-emerald-500' : 'text-slate-300'}`}
                      >
                        {hasLetters ? (
                          <Check
                            size={10}
                            strokeWidth={3}
                          />
                        ) : (
                          <X
                            size={10}
                            strokeWidth={3}
                          />
                        )}
                        <span className='text-[9px] font-black uppercase tracking-tight'>
                          {t(lang, 'passwordLetters')}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 ${hasNumbers ? 'text-emerald-500' : 'text-slate-300'}`}
                      >
                        {hasNumbers ? (
                          <Check
                            size={10}
                            strokeWidth={3}
                          />
                        ) : (
                          <X
                            size={10}
                            strokeWidth={3}
                          />
                        )}
                        <span className='text-[9px] font-black uppercase tracking-tight'>
                          {t(lang, 'passwordNumbers')}
                        </span>
                      </div>
                      <div
                        className={`flex items-center gap-1.5 ${hasSpecial ? 'text-emerald-500' : 'text-slate-300'}`}
                      >
                        {hasSpecial ? (
                          <Check
                            size={10}
                            strokeWidth={3}
                          />
                        ) : (
                          <X
                            size={10}
                            strokeWidth={3}
                          />
                        )}
                        <span className='text-[9px] font-black uppercase tracking-tight'>
                          {t(lang, 'passwordSpecial')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className='space-y-1.5'>
                <label className='text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] ml-1'>
                  {t(lang, 'confirmPassword')}
                </label>
                <div className='relative group'>
                  <KeyRound
                    className='absolute left-4 top-1/2 -translate-y-1/2 text-slate-300 group-focus-within:text-rose-500 transition-colors'
                    size={18}
                  />
                  <input
                    type='password'
                    placeholder='••••••••'
                    className={`w-full bg-slate-50 border ${passwordError ? 'border-rose-200' : passwordSuccess ? 'border-emerald-200' : 'border-slate-100'} rounded-2xl py-4 pl-12 pr-4 text-slate-900 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-rose-500/10 focus:border-rose-500 focus:bg-white transition-all placeholder:text-slate-200`}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={isLoading}
                  />
                </div>
                {passwordError && (
                  <p className='text-[9px] font-black text-rose-500 uppercase tracking-tight ml-1 animate-in fade-in slide-in-from-top-1'>
                    {passwordError}
                  </p>
                )}
                {passwordSuccess && (
                  <p className='text-[9px] font-black text-emerald-500 uppercase tracking-tight ml-1 animate-in fade-in slide-in-from-top-1'>
                    {passwordSuccess}
                  </p>
                )}
              </div>
            </div>

            {sameAsOldError && (
              <div className='bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 animate-in shake duration-300'>
                <AlertCircle
                  size={16}
                  className='text-rose-500 shrink-0'
                />
                <p className='text-rose-600 text-[11px] font-black leading-tight uppercase'>
                  {sameAsOldError}
                </p>
              </div>
            )}

            {error && (
              <div className='bg-rose-50 border border-rose-100 p-4 rounded-2xl flex items-center gap-3 animate-in shake duration-300'>
                <AlertCircle
                  size={16}
                  className='text-rose-500 shrink-0'
                />
                <p className='text-rose-600 text-[11px] font-black leading-tight uppercase'>
                  {error}
                </p>
              </div>
            )}

            <button
              type='submit'
              disabled={isLoading || !password || !confirmPassword}
              className='w-full bg-slate-900 text-white py-5 rounded-[24px] font-black text-xs uppercase tracking-[0.2em] md:hover:bg-rose-500 transition-all shadow-xl shadow-slate-100 disabled:opacity-70 flex items-center justify-center gap-3 active:scale-[0.98]'
            >
              {isLoading ? (
                <Loader2
                  className='animate-spin'
                  size={20}
                />
              ) : (
                t(lang, 'updatePassword')
              )}
            </button>

            <button
              type='button'
              onClick={onBack}
              className='w-full text-slate-400 font-black text-[10px] uppercase tracking-[0.2em] md:hover:text-slate-900 transition-colors flex items-center justify-center gap-2'
            >
              <ArrowLeft size={14} /> {t(lang, 'backToEntry')}
            </button>
          </form>
        </div>
      </div>
      <AppFooter lang={lang} />
    </div>
  )
}
